"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useEffectEvent, useState } from "react";
import { usePathname } from "next/navigation";
import { useIdentity } from "@/lib/providers/identity";
import { UNDERWRITER_SCOPE } from "@/features/underwriter/shell";
import { getEvent, useAnalytics } from "@/lib/analytics";
import { DEFAULT_PLATFORM_SCOPE, type PlatformScope } from "@/domain/scope/platform-scope";
import { firmScopeOf } from "@/domain/scope/request-scope";
import type { SpotlightView } from "@/contexts/feed/domain/spotlight/spotlight-settings";
import { spotlightSurfaceFor } from "../lib/spotlight-path-gate";
import { useHostReadiness } from "../model/use-host-readiness";
import { useReducedMotion } from "../model/use-reduced-motion";
import { useSpotlightActions } from "../model/use-spotlight-actions";
import { useSpotlightQueue } from "../model/use-spotlight-queue";
import { SPOTLIGHTS_PER_SITTING, useSpotlightSitting } from "../model/use-spotlight-sitting";
import { SpotlightLandingCoach } from "./spotlight-landing-coach";
import { SpotlightShowcase } from "./spotlight-showcase";

/**
 * The feed spotlight host, mounted ONCE in the root layout (spec
 * cova-feed/2026-10-09-001 §6.2).
 *
 * Spotlights ship on, with no feature flag: publishing a spotlight is the
 * switch (Tai, 2026-10-09). This parent reads only identity and the path, and
 * renders nothing while identity is loading or absent or the path is public,
 * onboarding or admin. Every other hook lives in the children, so such a page
 * makes no request at all.
 *
 * The root layout plus a path gate is the only single mount point: broker
 * pages bring their own DashboardLayout and the underwriter shell is not a
 * broker shell. A core-scoped identity whose firm is not a brokerage is the one
 * the underwriter shell wraps, so its surface is `underwriter`.
 */
export function FeedSpotlightHost() {
  const $ = _c(10);
  const {
    identity,
    isLoading
  } = useIdentity();
  const pathname = usePathname() ?? "/";
  if (isLoading || identity === null) {
    return null;
  }
  let t0;
  if ($[0] !== pathname) {
    t0 = spotlightSurfaceFor(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const gated = t0;
  let t1;
  if ($[2] !== gated || $[3] !== identity) {
    t1 = gated === DEFAULT_PLATFORM_SCOPE && firmScopeOf(identity) !== DEFAULT_PLATFORM_SCOPE ? UNDERWRITER_SCOPE : gated;
    $[2] = gated;
    $[3] = identity;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const surface = t1;
  let t2;
  if ($[5] !== surface) {
    t2 = <SpotlightQueueHost surface={surface} />;
    $[5] = surface;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Suspense fallback={null}><SpotlightLandingCoach /></Suspense>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2) {
    t4 = <>{t2}{t3}</>;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Shows `queue[0]` when the page is ready (idle, no dialog open, nobody
 * typing) and the sitting has room. Once shown, a spotlight stays until acted
 * on: it is a modal, so a route change keeps it open, and the path gate only
 * decides whether a NEW one may start.
 */
function SpotlightQueueHost(t0) {
  const $ = _c(33);
  const {
    surface
  } = t0;
  const [current, setCurrent] = useState(null);
  const querySurface = current?.surface ?? surface;
  const queue = useSpotlightQueue(querySurface);
  const ready = useHostReadiness();
  const sitting = useSpotlightSitting();
  const reduced = useReducedMotion();
  const actions = useSpotlightActions(querySurface ?? DEFAULT_PLATFORM_SCOPE);
  const {
    track
  } = useAnalytics();
  const head = queue[0] ?? null;
  const canStart = current === null && surface !== null && ready && sitting.room > 0 && head !== null;
  if (canStart && head !== null && surface !== null) {
    setCurrent({
      view: head,
      surface,
      position: sitting.count + 1
    });
  }
  let t1;
  if ($[0] !== sitting || $[1] !== track) {
    t1 = shown => {
      sitting.record();
      track(getEvent("feed-spotlight", "spotlight_shown"), {
        postId: shown.view.postId,
        version: shown.view.version,
        ctaKind: shown.view.cta.kind === "open" ? shown.view.cta.action.kind : null,
        ctaState: shown.view.cta.kind,
        surface: shown.surface,
        positionInSitting: shown.position
      });
    };
    $[0] = sitting;
    $[1] = track;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onShown = useEffectEvent(t1);
  let t2;
  if ($[3] !== current || $[4] !== onShown) {
    t2 = () => {
      if (current !== null) {
        onShown(current);
      }
    };
    $[3] = current;
    $[4] = onShown;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== current) {
    t3 = [current];
    $[6] = current;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  if (current === null) {
    return null;
  }
  const {
    view,
    position
  } = current;
  let t4;
  if ($[8] !== position || $[9] !== queue || $[10] !== view.postId) {
    t4 = position < SPOTLIGHTS_PER_SITTING ? queue.find(item => item.postId !== view.postId) ?? null : null;
    $[8] = position;
    $[9] = queue;
    $[10] = view.postId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const next = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setCurrent(null);
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const close = t5;
  let t6;
  if ($[13] !== current.surface || $[14] !== position || $[15] !== view.postId || $[16] !== view.version) {
    t6 = {
      postId: view.postId,
      version: view.version,
      surface: current.surface,
      positionInSitting: position
    };
    $[13] = current.surface;
    $[14] = position;
    $[15] = view.postId;
    $[16] = view.version;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const props = t6;
  let t7;
  let t8;
  let t9;
  if ($[18] !== actions || $[19] !== props || $[20] !== track || $[21] !== view) {
    t7 = () => {
      track(getEvent("feed-spotlight", "spotlight_dismissed"), props);
      actions.dismiss(view, close);
    };
    t8 = () => {
      track(getEvent("feed-spotlight", "spotlight_dismissed"), {
        ...props,
        via: "up_next"
      });
      actions.dismiss(view, close);
    };
    t9 = () => {
      const cta = view.cta;
      const destinationId = cta.kind === "open" ? cta.action.kind === "route" ? cta.action.destinationId : null : cta.destinationId;
      const event = cta.kind === "request" ? getEvent("feed-spotlight", "spotlight_access_requested") : cta.kind === "upgrade" ? getEvent("feed-spotlight", "spotlight_upgrade_clicked") : getEvent("feed-spotlight", "spotlight_opened");
      track(event, {
        ...props,
        destinationId,
        ctaKind: cta.kind === "open" ? cta.action.kind : null,
        ctaState: cta.kind
      });
      actions.act(view, close);
    };
    $[18] = actions;
    $[19] = props;
    $[20] = track;
    $[21] = view;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t10;
  if ($[25] !== actions.busy || $[26] !== next || $[27] !== reduced || $[28] !== t7 || $[29] !== t8 || $[30] !== t9 || $[31] !== view) {
    t10 = <SpotlightShowcase spotlight={view} next={next} reduced={reduced} busy={actions.busy} onDismiss={t7} onNext={t8} onAct={t9} />;
    $[25] = actions.busy;
    $[26] = next;
    $[27] = reduced;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = view;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}